/**
 * ReviewStoryPanel — post-publish surfacing controls.
 *
 * Lets the author confirm/edit the AI-suggested dimensions and highlight
 * that determine how the story is found and previewed. Designed to be
 * embedded in:
 *   - ReviewStoryPage (full-page after submit)
 *   - EditStoryPage (deep-link from the metadata edit affordance)
 *
 * Controlled component — parent owns state and supplies callbacks.
 *
 * Props:
 *   story:         DTO from toPublicStoryDto (used for read-only preview)
 *   selected:      { life_areas: Set, inner_knots: Set, ... } per FILTER_DIMENSIONS
 *                  excluding practices (review panel covers AI-extracted dims only)
 *   tagOptions:    { life_areas: [...], ... } from useTagOptions / listTags
 *   highlight:     string (current draft of the 2-3 line hook)
 *   suggestedHighlight: string | null (a fresh re-analyze highlight that
 *                  differs from the draft; surfaced as Use/Keep-mine)
 *   onToggleTag:   (dimensionKey, tagId) => void
 *   onHighlightChange: (next: string) => void
 *   onUseSuggestedHighlight:     () => void  (apply the suggestion)
 *   onDismissSuggestedHighlight: () => void  (keep the current draft)
 *   saving:        boolean
 *   onSave:        () => void
 *   onSkip:        () => void  (optional — "do this later")
 *   onReanalyze:   () => void  (optional — re-run Gemini on the story text)
 *   reanalyzing:   boolean     (optional — disables Re-analyze while inflight)
 *   saveLabel:     string      (optional, defaults to "Save & finish")
 */

import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FILTER_DIMENSIONS } from '@/lib/filters';
import DimensionInfoButton from '@/components/DimensionInfoButton';
import {
  STORY_REANALYZE_LOADING_MESSAGES,
  STORY_REANALYZE_LOADING_INTERVAL_MS,
  STORY_REANALYZE_LOADING_HINT,
  STORY_WRITE_LOADING_MESSAGES,
  STORY_WRITE_LOADING_INTERVAL_MS,
  STORY_WRITE_LOADING_HINT,
} from '@/lib/storyFormLoadingMessages';

const HIGHLIGHT_MAX = 240;

// Surface every dimension authors can edit — including practices, so the
// reader can correct AI's practice picks (or add ones it missed) without
// leaving the review flow.
const REVIEW_DIMENSIONS = FILTER_DIMENSIONS;

const tagChipClasses =
  'rounded-full border transition-colors text-sm leading-normal px-3 py-1.5';

export default function ReviewStoryPanel({
  story,
  selected,
  tagOptions,
  highlight,
  suggestedHighlight = null,
  onToggleTag,
  onHighlightChange,
  onUseSuggestedHighlight,
  onDismissSuggestedHighlight,
  saving = false,
  onSave,
  onSkip,
  onReanalyze,
  reanalyzing = false,
  saveLabel = 'Save & finish',
}) {
  const remaining = HIGHLIGHT_MAX - (highlight?.length || 0);
  const overLimit = remaining < 0;

  // Rotating copy during re-analyze — Gemini cold-start is ~5s, so a single
  // static "Analyzing…" feels stuck. Reset to 0 whenever the flag flips off.
  const [reanalyzeMsgIdx, setReanalyzeMsgIdx] = useState(0);
  useEffect(() => {
    if (!reanalyzing) {
      setReanalyzeMsgIdx(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setReanalyzeMsgIdx((i) => (i + 1) % STORY_REANALYZE_LOADING_MESSAGES.length);
    }, STORY_REANALYZE_LOADING_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reanalyzing]);
  const reanalyzeMsg = STORY_REANALYZE_LOADING_MESSAGES[reanalyzeMsgIdx];

  // Same rotating pattern for the save button — saves go through
  // km_update_story which is fast when warm but slow on cold start.
  const [saveMsgIdx, setSaveMsgIdx] = useState(0);
  useEffect(() => {
    if (!saving) {
      setSaveMsgIdx(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setSaveMsgIdx((i) => (i + 1) % STORY_WRITE_LOADING_MESSAGES.length);
    }, STORY_WRITE_LOADING_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [saving]);

  return (
    <div className="space-y-8" data-testid="review-story-panel">
      <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-3">
        <div>
          <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Highlight</h3>
          <p className="text-sm text-[#6B665E] mt-1">
            Two or three lines readers see before they click in — share cards, feed previews.
            We seeded one from your story; edit freely.
          </p>
        </div>
        <Textarea
          value={highlight || ''}
          onChange={(e) => onHighlightChange(e.target.value)}
          placeholder="The emotional hook of your story, in your own words…"
          className="min-h-28"
          maxLength={HIGHLIGHT_MAX + 60}
          data-testid="review-highlight-input"
        />
        <div
          className={
            'text-xs text-right ' + (overLimit ? 'text-[#C05621]' : 'text-[#6B665E]')
          }
          data-testid="review-highlight-counter"
        >
          {Math.max(0, remaining)} characters left
          {overLimit && ' (will be trimmed on save)'}
        </div>

        {suggestedHighlight && (
          <div
            className="rounded-lg border border-[#C05621]/30 bg-[#FBF4EB] p-3 space-y-2"
            data-testid="review-highlight-suggestion"
          >
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C05621]">
              <Sparkles className="h-3.5 w-3.5" /> Suggested highlight
            </div>
            <p className="text-sm text-[#2D2A26] leading-relaxed italic">
              {suggestedHighlight}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onUseSuggestedHighlight}
                className="rounded-full bg-[#C05621] hover:bg-[#A0461A] text-white text-sm font-medium px-4 py-1.5"
                data-testid="review-highlight-suggestion-use"
              >
                Use this
              </button>
              <button
                type="button"
                onClick={onDismissSuggestedHighlight}
                className="rounded-full border border-[#E6E2D6] text-[#2D2A26] hover:border-[#C05621] text-sm font-medium px-4 py-1.5"
                data-testid="review-highlight-suggestion-dismiss"
              >
                Keep mine
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Themes</h3>
            <p className="text-sm text-[#6B665E] mt-1">
              We suggested some — keep what fits, remove what doesn't, add anything missing.
              These help the right readers find your story.
            </p>
          </div>
          {onReanalyze && (
            <button
              type="button"
              onClick={onReanalyze}
              disabled={reanalyzing || saving}
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-[#C05621] hover:text-[#A0461A] disabled:opacity-60 disabled:cursor-not-allowed underline-offset-2 hover:underline"
              data-testid="review-reanalyze-btn"
            >
              <Sparkles className={'h-4 w-4 ' + (reanalyzing ? 'animate-pulse' : '')} />
              {reanalyzing ? reanalyzeMsg : 'Re-analyze'}
            </button>
          )}
        </div>
        {reanalyzing && (
          <p
            className="text-xs text-[#6B665E] -mt-2"
            data-testid="review-reanalyze-hint"
          >
            {STORY_REANALYZE_LOADING_HINT}
          </p>
        )}

        {REVIEW_DIMENSIONS.map(({ key, label }) => {
          const options = tagOptions[key] || [];
          if (!options.length) return null;
          return (
            <div key={key} data-testid={`review-dim-${key}`}>
              <Label className="text-sm font-semibold text-[#2D2A26] mb-2 inline-flex items-center gap-1.5">
                {label}
                <DimensionInfoButton dimensionKey={key} />
              </Label>
              <div className="flex flex-wrap gap-2">
                {options.map((tag) => {
                  const isOn = selected[key]?.has(tag.id);
                  return (
                    <button
                      key={`${tag.dimension}-${tag.id}`}
                      type="button"
                      onClick={() => onToggleTag(key, tag.id)}
                      className={
                        tagChipClasses +
                        ' ' +
                        (isOn
                          ? 'bg-[#C05621] text-white border-[#C05621]'
                          : 'bg-[#FDFBF7] text-[#2D2A26] border-[#E6E2D6] hover:border-[#C05621]')
                      }
                      data-testid={`review-tag-${key}-${tag.id}`}
                    >
                      {tag.display_name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        {onSkip && (
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
            disabled={saving}
            className="sm:flex-1 border-[#E6E2D6]"
            data-testid="review-skip-btn"
          >
            Do this later
          </Button>
        )}
        <Button
          type="button"
          onClick={onSave}
          disabled={saving || reanalyzing}
          className="sm:flex-1 rounded-full px-8 py-6 text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl"
          data-testid="review-save-btn"
        >
          {saving ? STORY_WRITE_LOADING_MESSAGES[saveMsgIdx] : saveLabel}
        </Button>
      </div>
      {saving && (
        <p
          className="text-sm text-[#6B665E] text-center"
          data-testid="review-save-hint"
        >
          {STORY_WRITE_LOADING_HINT}
        </p>
      )}
    </div>
  );
}

export { HIGHLIGHT_MAX, REVIEW_DIMENSIONS };
